import { memo, useCallback } from "react";
import { useAsyncOperationProgress } from "./useAsyncOperationProgress";
import { AsyncOperationProgressStep } from "@/components/progress/AsyncOperationProgressStep";
import { AsyncOperationCompletedStep } from "@/components/progress/AsyncOperationCompletedStep";

export enum OperationProgressStatus {
  IDLE = "idle",
  PROCESSING = "processing",
  CANCELLING = "cancelling",
  COMPLETED = "completed",
  CANCELLED = "cancelled",
}

export interface OperationProgressItem {
  id: string;
  label: string;
  success?: boolean;
  errorDescription?: string;
}

export interface AsyncOperationProgressProps {
  currentItemLabel?: string;
  operationProgress: ReturnType<typeof useAsyncOperationProgress>;
  /**
   * Base translation key for all operation progress text.
   * Will look for nested keys:
   * - `${translationKey}.processing.title` (receives {current} interpolation)
   * - `${translationKey}.processing.description` (receives {processed, total} interpolation)
   * - `${translationKey}.processing.cancelButton`
   * - `${translationKey}.completed.title`
   * - `${translationKey}.completed.description` (receives {processed, total} interpolation)
   * - `${translationKey}.completed.goBack`
   * - `${translationKey}.completed.close`
   */
  translationKey?: string;
  /** Callback to close the dialog/modal - back button click */
  onReset?: () => void;
  /** Callback to close the dialog/modal - close button click */
  onClose?: () => void;
  /** Callback to cancel the operation - cancel button click */
  onCancel?: () => void;
  /** Custom render function for completed items */
  renderCompletedItem?: (item: OperationProgressItem) => React.ReactNode;
  /** Additional values to pass to translation interpolation */
  i18nMetadata?: Record<string, unknown>;
}

const AsyncOperationProgressComponent = ({
  operationProgress,
  currentItemLabel,
  onReset,
  translationKey = "operations",
  onClose,
  onCancel,
  renderCompletedItem,
  i18nMetadata,
}: AsyncOperationProgressProps) => {
  const { status, processedCount, totalCount, items } = operationProgress;

  const onCancelInternal = useCallback(() => {
    operationProgress.cancel();
    onCancel?.();
  }, [operationProgress, onCancel]);

  const onResetInternal = useCallback(() => {
    operationProgress.reset();
    onReset?.();
  }, [operationProgress, onReset]);

  const onCloseInternal = useCallback(() => {
    operationProgress.reset();
    onClose?.();
  }, [operationProgress, onClose]);

  if (operationProgress.status === OperationProgressStatus.IDLE) {
    return null;
  }

  const isCompleted =
    status === OperationProgressStatus.COMPLETED ||
    status === OperationProgressStatus.CANCELLED;

  const isBusy =
    status === OperationProgressStatus.PROCESSING ||
    status === OperationProgressStatus.CANCELLING;

  if (isBusy) {
    return (
      <AsyncOperationProgressStep
        currentItem={currentItemLabel ?? ""}
        processedItems={processedCount}
        totalItems={totalCount}
        onCancel={onCancelInternal}
        processingTitleKey={`${translationKey}.processing.title`}
        processingDescriptionKey={`${translationKey}.processing.description`}
        processingCancelButtonKey={`${translationKey}.processing.cancelButton`}
        i18nMetadata={i18nMetadata}
      />
    );
  }

  if (isCompleted) {
    return (
      <AsyncOperationCompletedStep
        processedItems={processedCount}
        totalItems={totalCount}
        data={items}
        onReset={onReset ? onResetInternal : undefined}
        completedTitleKey={`${translationKey}.completed.title`}
        completedDescriptionKey={`${translationKey}.completed.description`}
        completedGoBackButtonKey={`${translationKey}.completed.goBack`}
        completedCloseButtonKey={`${translationKey}.completed.close`}
        onClose={onCloseInternal}
        renderCompletedItem={renderCompletedItem}
        i18nMetadata={i18nMetadata}
      />
    );
  }
  return null;
};

export const AsyncOperationProgress = memo(AsyncOperationProgressComponent);
